/**
 * LedgerTrackA.jsx — client-readable friendly components for /ledger-inspection.
 *
 * Every component in this file is the "friendly" counterpart to the raw admin view.
 * No raw enum values are ever rendered as visible text. Internal IDs (stream_id, doc_id,
 * event_id) live behind a RowDetailsPopover, never in the default table.
 *
 * GUARDRAIL: UpdateHoldingDialog's tabs are the ONLY way to submit inputs.
 * The four allowed actions are: add_line(capital_call_event_amount),
 * add_line(distribution_event_amount), supply_nav, assert_close.
 * There is NO input control for XIRR / TVPI / DPI / RVPI — those are computed outputs.
 */

import { useState } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { ledgerFetch } from "@/lib/ledgerDemoSource";
import {
  EVENT_TYPE_LABELS,
  TIER_CONFIDENCE,
  NAV_STATUS_LABELS,
  FLAG_HINTS,
  METRIC_TOOLTIPS,
  eventTypeLabel,
  confidenceForTier,
  metricGapReason,
  fmtDate,
  fmtPct,
  fmtMultiple,
  fmtAmount,
} from "@/lib/ledgerTrackA";

// ── EventTypeLabel ────────────────────────────────────────────────────────────

/** Renders a human-readable label for a raw event type key. */
export function EventTypeLabel({ type }) {
  const label = eventTypeLabel(type);
  return <span className="font-medium text-gray-800">{label}</span>;
}

// ── ConfidencePill ────────────────────────────────────────────────────────────

const TIER_HINTS = {
  emerald: "All figures have been reconciled against fund records.",
  indigo: "Figures are computed from extracted data. Verify with your fund statements.",
  amber: "Some inputs are missing. Complete them to get full performance metrics.",
  gray: "Confidence level unknown.",
};

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-300",
  amber: "bg-amber-50 text-amber-700 border-amber-400",
  gray: "bg-gray-100 text-gray-600 border-gray-300",
};

/** Badge showing the confidence tier with a hover-card explanation. */
export function ConfidencePill({ tier }) {
  const conf = confidenceForTier(tier);
  const cls = TONE_CLASSES[conf.tone] ?? TONE_CLASSES.gray;
  const hint = TIER_HINTS[conf.tone] ?? TIER_HINTS.gray;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-semibold cursor-default select-none",
            cls
          )}
          data-testid="confidence-pill"
        >
          {conf.label}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-56 text-xs text-gray-700 leading-relaxed">
        {hint}
      </HoverCardContent>
    </HoverCard>
  );
}

// ── MetricTooltip ─────────────────────────────────────────────────────────────

/** Wraps a metric label in a hover card tooltip explaining what the metric means. */
export function MetricTooltip({ metricKey, children }) {
  const tip = METRIC_TOOLTIPS[metricKey];
  if (!tip) return <>{children}</>;
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          className="cursor-default underline decoration-dotted decoration-gray-400"
          data-metric={metricKey}
        >
          {children}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-64 text-xs text-gray-700 leading-relaxed">
        {tip}
      </HoverCardContent>
    </HoverCard>
  );
}

// ── ProvenancePill ────────────────────────────────────────────────────────────

/** Shows whether a row came from document extraction or was added by the user. */
export function ProvenancePill({ source }) {
  if (source === "client") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-300">
        You added
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
      Extracted
    </span>
  );
}

// ── RowDetailsPopover ─────────────────────────────────────────────────────────

/**
 * A "Details" trigger that opens a popover revealing internal row identifiers:
 * event_id, doc_id, stream_id, basis, scope, source_ref, gate_reason.
 * These are machinery fields — not shown in the default table.
 */
export function RowDetailsPopover({ row }) {
  const fields = [
    ["event_id", row.event_id],
    ["doc_id", row.doc_id],
    ["stream_id", row.stream_id],
    ["basis", row.basis],
    ["scope", row.scope],
    ["source_ref", row.source_ref],
    ["gate_reason", row.gate_reason],
  ].filter(([, v]) => v != null && v !== "");

  if (fields.length === 0) return <span className="text-gray-300 text-[10px]">—</span>;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-[10px] text-indigo-600 hover:text-indigo-800 underline transition-colors focus:outline-none"
          aria-label="Show row details"
        >
          Details
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Row details
          </p>
          {fields.map(([key, value]) => (
            <div key={key} className="flex items-start gap-2">
              <span className="text-[10px] font-mono text-gray-400 min-w-[80px] shrink-0">{key}</span>
              <span className="text-[10px] font-mono text-gray-800 break-all">{String(value)}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── FriendlyMetricsCard ───────────────────────────────────────────────────────

/** A client-readable metrics card: replaces raw TierBadge/NavStatusBadge with friendly labels. */
export function FriendlyMetricsCard({ m }) {
  if (!m) return null;

  const dpiFmt = fmtMultiple(m.dpi);
  const tvpiFmt = fmtMultiple(m.tvpi);
  const rvpiFmt = fmtMultiple(m.rvpi);
  const xirrFmt = fmtPct(m.xirr);

  const tierBorder =
    m.tier === "book_verified"
      ? "border-emerald-300 bg-emerald-50/40"
      : m.tier === "computed_only"
      ? "border-indigo-200 bg-indigo-50/30"
      : m.tier === "honest_null"
      ? "border-amber-300 bg-amber-50/40"
      : "border-gray-200 bg-gray-50/40";

  function MetricCell({ metricKey, value, gapReason }) {
    return (
      <div className="flex flex-col items-center min-w-[80px] px-3 py-2 rounded border border-gray-200 bg-white">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">
          <MetricTooltip metricKey={metricKey}>{metricKey}</MetricTooltip>
        </span>
        {value != null ? (
          <span className="text-sm font-mono font-bold text-gray-900">{value}</span>
        ) : (
          <span
            className="text-[10px] text-gray-400 italic text-center leading-tight"
            title={gapReason || ""}
          >
            {gapReason ? gapReason.slice(0, 32) + (gapReason.length > 32 ? "…" : "") : "—"}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("rounded-md border px-3 py-2.5 mb-3 flex flex-col gap-2", tierBorder)}>
      {/* Confidence pill row */}
      <div className="flex items-center gap-2 flex-wrap">
        <ConfidencePill tier={m.tier} />
        {m.book_xirr != null && (
          <span className="text-[10px] font-mono text-emerald-700">
            Book IRR: {m.book_xirr}
          </span>
        )}
      </div>

      {/* Metric cells */}
      <div className="flex flex-wrap gap-2">
        <MetricCell
          metricKey="DPI"
          value={dpiFmt}
          gapReason={metricGapReason("DPI", m)}
        />
        <MetricCell
          metricKey="TVPI"
          value={tvpiFmt}
          gapReason={metricGapReason("TVPI", m)}
        />
        <MetricCell
          metricKey="RVPI"
          value={rvpiFmt}
          gapReason={metricGapReason("RVPI", m)}
        />
        <MetricCell
          metricKey="XIRR"
          value={xirrFmt}
          gapReason={metricGapReason("XIRR", m)}
        />
      </div>

      {/* Amounts row */}
      {(m.commitment != null || m.called != null || m.distributed != null || m.nav != null) && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-gray-600">
          {m.commitment != null && (
            <span>Commitment: {fmtAmount(m.commitment, m.currency)}</span>
          )}
          {m.called != null && (
            <span>Invested: {fmtAmount(m.called, m.currency)}</span>
          )}
          {m.distributed != null && (
            <span>Distributed: {fmtAmount(m.distributed, m.currency)}</span>
          )}
          {m.nav != null && (
            <span>Current value: {fmtAmount(m.nav, m.currency)}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── FriendlyMemberTable ───────────────────────────────────────────────────────

/**
 * 6-column table for client display.
 * - gate_rejected rows are hidden (they appear only in raw admin view)
 * - Merged extracted + client rows, sorted by event_date (same logic as raw MemberTable)
 * - Columns: Date · Event · Amount · Currency · Source · Details
 */
export function FriendlyMemberTable({ rows, clientInputs }) {
  // Merge extracted (excluding gate_rejected) + client rows, date-sorted
  const mergedRows = [
    ...rows
      .filter((row) => row.row_class !== "gate_rejected")
      .map((row, i) => ({ kind: "ext", row, key: `r-${i}`, date: row.event_date || "" })),
    ...(clientInputs || []).map((ci) => ({
      kind: "ci",
      ci,
      key: `ci-${ci.id}`,
      date: ci.event_date || "",
    })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  if (mergedRows.length === 0) {
    return (
      <p className="text-xs text-gray-400 italic px-2 py-3">No activity recorded yet.</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">Date</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">Event</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-500 whitespace-nowrap">Amount</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">Currency</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">Source</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">Details</th>
          </tr>
        </thead>
        <tbody>
          {mergedRows.map((item, idx) => {
            if (item.kind === "ci") {
              const ci = item.ci;
              return (
                <tr
                  key={item.key}
                  className="border-b border-blue-100 bg-blue-50/30"
                  style={{ borderLeft: "3px solid #3b82f6" }}
                >
                  <td className="px-3 py-1.5 whitespace-nowrap text-blue-700 font-medium">
                    {fmtDate(ci.event_date)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <EventTypeLabel type={ci.type} />
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right text-blue-700">
                    {fmtAmount(ci.amount, ci.currency)}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-blue-600">
                    {ci.currency || "—"}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <ProvenancePill source="client" />
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <RowDetailsPopover
                      row={{
                        stream_id: ci.stream_id,
                        basis: ci.nav_source
                          ? `client_input · nav_source=${ci.nav_source}`
                          : ci.note || "client_input",
                      }}
                    />
                  </td>
                </tr>
              );
            }

            const row = item.row;
            const rowBg = idx % 2 === 0 ? "bg-white" : "bg-gray-50/30";
            return (
              <tr key={item.key} className={cn("border-b border-gray-100", rowBg)}>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">
                  {fmtDate(row.event_date)}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <EventTypeLabel type={row.type} />
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right text-gray-700">
                  {fmtAmount(row.amount, row.currency)}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">
                  {row.currency || "—"}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <ProvenancePill source="extracted" />
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <RowDetailsPopover row={row} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── NeedsNavCta ───────────────────────────────────────────────────────────────

/** Amber banner shown when nav_status === "needs_nav". */
export function NeedsNavCta() {
  return (
    <div className="mb-3 rounded-md border border-amber-400 bg-amber-50 px-3 py-2.5">
      <p className="text-xs font-semibold text-amber-800">
        Add current NAV
      </p>
      <p className="text-[11px] text-amber-700 mt-0.5">
        No current valuation is on file. Add the latest NAV from your fund statement
        to unlock TVPI and return metrics. Use "Update holdings" below.
      </p>
    </div>
  );
}

// ── UpdateHoldingDialog ───────────────────────────────────────────────────────

/**
 * Dialog with four tabs for client input actions.
 * The tabs ARE the input allowlist — no output-metric (XIRR/TVPI/DPI/RVPI) field exists.
 *
 * Capital Call   → add_line { type: "capital_call_event_amount" }
 * Distribution   → add_line { type: "distribution_event_amount" }
 * Current NAV    → supply_nav
 * Mark closed    → assert_close
 */
export function UpdateHoldingDialog({ streamId, currency, token, onResult }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Capital Call fields
  const [ccDate, setCcDate] = useState("");
  const [ccAmount, setCcAmount] = useState("");

  // Distribution fields
  const [distDate, setDistDate] = useState("");
  const [distAmount, setDistAmount] = useState("");

  // NAV fields
  const [navDate, setNavDate] = useState("");
  const [navAmount, setNavAmount] = useState("");

  const inputCls =
    "h-7 px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-300 w-full";

  async function postInput(body) {
    setSubmitting(true);
    setErr(null);
    try {
      const res = await ledgerFetch("/api/ledger/poc-inspection/input", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ stream_id: streamId, ...body }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ": " + txt.slice(0, 120) : ""}`);
      }
      const json = await res.json();
      if (onResult) onResult(json);
      setOpen(false);
      return json;
    } catch (e) {
      setErr(e?.message || "request failed");
      return null;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCapitalCall(e) {
    e.preventDefault();
    if (!ccDate || !ccAmount) return;
    const ok = await postInput({
      action: "add_line",
      type: "capital_call_event_amount",
      event_date: ccDate,
      amount: parseFloat(ccAmount),
      currency: currency || undefined,
    });
    if (ok) { setCcDate(""); setCcAmount(""); }
  }

  async function handleDistribution(e) {
    e.preventDefault();
    if (!distDate || !distAmount) return;
    const ok = await postInput({
      action: "add_line",
      type: "distribution_event_amount",
      event_date: distDate,
      amount: parseFloat(distAmount),
      currency: currency || undefined,
    });
    if (ok) { setDistDate(""); setDistAmount(""); }
  }

  async function handleNav(e) {
    e.preventDefault();
    if (!navDate || !navAmount) return;
    const ok = await postInput({
      action: "supply_nav",
      event_date: navDate,
      amount: parseFloat(navAmount),
      currency: currency || undefined,
    });
    if (ok) { setNavDate(""); setNavAmount(""); }
  }

  async function handleMarkClosed() {
    await postInput({
      action: "assert_close",
      event_date: new Date().toISOString().slice(0, 10),
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-xs font-medium text-indigo-700 border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 rounded px-3 py-1.5 transition-colors"
          aria-label="Update holdings"
        >
          Update holdings
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-gray-900">
            Update holdings
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="capital_call" className="mt-1">
          <TabsList className="grid grid-cols-4 w-full h-8">
            <TabsTrigger value="capital_call" className="text-[11px]">
              Capital Call
            </TabsTrigger>
            <TabsTrigger value="distribution" className="text-[11px]">
              Distribution
            </TabsTrigger>
            <TabsTrigger value="nav" className="text-[11px]">
              Current NAV
            </TabsTrigger>
            <TabsTrigger value="close" className="text-[11px]">
              Mark closed
            </TabsTrigger>
          </TabsList>

          {/* ── Capital Call tab ── */}
          <TabsContent value="capital_call" className="pt-3">
            <form onSubmit={handleCapitalCall} className="space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
                  Call date
                </label>
                <input
                  type="date"
                  value={ccDate}
                  onChange={(e) => setCcDate(e.target.value)}
                  className={inputCls}
                  aria-label="Capital call date"
                  required
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
                  Amount ({currency || "USD"})
                </label>
                <input
                  type="number"
                  value={ccAmount}
                  onChange={(e) => setCcAmount(e.target.value)}
                  placeholder="Amount"
                  className={inputCls}
                  aria-label="Capital call amount"
                  step="any"
                  min="0"
                  required
                  disabled={submitting}
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !ccDate || !ccAmount}
                className="w-full h-8 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Saving…" : "Add Capital Call"}
              </button>
            </form>
          </TabsContent>

          {/* ── Distribution tab ── */}
          <TabsContent value="distribution" className="pt-3">
            <form onSubmit={handleDistribution} className="space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
                  Distribution date
                </label>
                <input
                  type="date"
                  value={distDate}
                  onChange={(e) => setDistDate(e.target.value)}
                  className={inputCls}
                  aria-label="Distribution date"
                  required
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
                  Amount ({currency || "USD"})
                </label>
                <input
                  type="number"
                  value={distAmount}
                  onChange={(e) => setDistAmount(e.target.value)}
                  placeholder="Amount"
                  className={inputCls}
                  aria-label="Distribution amount"
                  step="any"
                  min="0"
                  required
                  disabled={submitting}
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !distDate || !distAmount}
                className="w-full h-8 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Saving…" : "Add Distribution"}
              </button>
            </form>
          </TabsContent>

          {/* ── Current NAV tab ── */}
          <TabsContent value="nav" className="pt-3">
            <form onSubmit={handleNav} className="space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
                  Valuation date
                </label>
                <input
                  type="date"
                  value={navDate}
                  onChange={(e) => setNavDate(e.target.value)}
                  className={inputCls}
                  aria-label="NAV date"
                  required
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
                  Current NAV ({currency || "USD"})
                </label>
                <input
                  type="number"
                  value={navAmount}
                  onChange={(e) => setNavAmount(e.target.value)}
                  placeholder="Current NAV value"
                  className={inputCls}
                  aria-label="NAV amount"
                  step="any"
                  min="0"
                  required
                  disabled={submitting}
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !navDate || !navAmount}
                className="w-full h-8 rounded text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Saving…" : "Save NAV"}
              </button>
            </form>
          </TabsContent>

          {/* ── Mark closed tab ── */}
          <TabsContent value="close" className="pt-3">
            <div className="space-y-3">
              <p className="text-xs text-gray-600">
                Mark this position as closed. This records today as the exit date and adds a
                realized-position marker. The action is reversible from the admin view.
              </p>
              <button
                type="button"
                onClick={handleMarkClosed}
                disabled={submitting}
                className="w-full h-8 rounded text-xs font-medium border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Saving…" : "Close position"}
              </button>
            </div>
          </TabsContent>
        </Tabs>

        {err && (
          <p className="text-xs text-red-600 font-mono bg-red-50 border border-red-200 rounded px-2 py-1 mt-2">
            {err}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── AdminViewToggle ───────────────────────────────────────────────────────────

/**
 * Admin-only Switch that toggles between client-friendly and raw inspection views.
 * The toggle and its label are never rendered for non-admin users.
 * Persists choice to localStorage under key "ledger_inspection_raw".
 */
export function AdminViewToggle({ rawMode, onChange }) {
  return (
    <div
      className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-1.5"
      data-testid="admin-view-toggle"
    >
      <span className="text-[10px] uppercase tracking-wider font-medium text-gray-500">
        Admin view
      </span>
      <Switch
        checked={rawMode}
        onCheckedChange={onChange}
        aria-label="Toggle raw inspection view"
      />
      <span className="text-xs text-gray-600 font-medium">
        {rawMode ? "Raw data" : "Client view"}
      </span>
    </div>
  );
}
